import { useState, useEffect, useMemo } from 'react';
import { CheckCircle, RefreshCw, ChevronRight } from 'lucide-react';
import Modal from '../common/Modal';
import { useToast } from '../common/ToastContainer';
import { routingPolicyService, appDeviceService } from '../../services';
import type { RoutingPolicy } from '../../services';
import type { EdgeDestinations } from '../../services/routingPolicy.service';
import { useTenantStore } from '../../stores/tenantStore';

interface RoutableSubnet {
  subnet: string;
  source: string;
  edgeNames: string[];
}

interface ApplyDgapTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupId: string;
  groupName: string;
  edgeGuid?: string | null;
  onApplied: () => void;
}

const ApplyDgapTemplateModal = ({ isOpen, onClose, groupId, groupName, edgeGuid, onApplied }: ApplyDgapTemplateModalProps) => {
  const toast = useToast();
  const selectedTenantId = useTenantStore((state) => state.selectedTenantId);
  const [templates, setTemplates] = useState<RoutingPolicy[]>([]);
  const [routableSubnets, setRoutableSubnets] = useState<RoutableSubnet[]>([]);
  const [destinations, setDestinations] = useState<EdgeDestinations | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  // Step 1: select template, Step 2: configure rule overrides
  const [step, setStep] = useState<1 | 2>(1);
  const [selectedTemplate, setSelectedTemplate] = useState<RoutingPolicy | null>(null);
  const [ruleOverrides, setRuleOverrides] = useState<Record<number, { destination_subnet?: string }>>({});

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedTemplate(null);
      setRuleOverrides({});
      fetchTemplates();
    }
  }, [isOpen]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await routingPolicyService.list({
        pageSize: 100,
        tenantId: selectedTenantId,
        scope: 'dg_template',
      });
      setTemplates(response.items);
    } catch (error) {
      console.error('Failed to fetch DGAP templates:', error);
      toast.error('Load Failed', 'Failed to load access policy templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = async (template: RoutingPolicy) => {
    try {
      setLoading(true);
      // Fetch full template with rules
      const fullTemplate = await routingPolicyService.getById(template.id);
      setSelectedTemplate(fullTemplate);

      // Fetch destinations context (preferred) or fall back to legacy subnets
      if (edgeGuid) {
        try {
          const dest = await routingPolicyService.getEdgeDestinations(edgeGuid);
          setDestinations(dest);
        } catch {
          // Fall back to legacy endpoint
          const subnets = await appDeviceService.getRoutableSubnets(groupId);
          setRoutableSubnets(subnets || []);
        }
      } else {
        const subnets = await appDeviceService.getRoutableSubnets(groupId);
        setRoutableSubnets(subnets || []);
      }

      // Initialize rule overrides with empty values
      const overrides: Record<number, { destination_subnet?: string }> = {};
      if (fullTemplate.rules) {
        for (const rule of fullTemplate.rules) {
          overrides[rule.rule_order] = {
            destination_subnet: rule.destination_subnet || '',
          };
        }
      }
      setRuleOverrides(overrides);
      setStep(2);
    } catch (error) {
      console.error('Failed to load template details:', error);
      toast.error('Load Failed', 'Failed to load template details or edge subnets');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!selectedTemplate) return;
    try {
      setApplying(true);
      await appDeviceService.applyDgapTemplate(groupId, selectedTemplate.id, ruleOverrides);
      toast.success('Template Applied', `Access policy template applied to "${groupName}". The policy starts disabled — enable it when ready.`);
      onApplied();
    } catch (error: any) {
      console.error('Failed to apply DGAP template:', error);
      const msg = error?.response?.data?.error?.message || error?.response?.data?.error || 'Failed to apply template';
      toast.error('Apply Failed', typeof msg === 'string' ? msg : 'Failed to apply template');
    } finally {
      setApplying(false);
    }
  };

  const handleOverrideChange = (ruleOrder: number, subnet: string) => {
    setRuleOverrides(prev => ({
      ...prev,
      [ruleOrder]: { destination_subnet: subnet },
    }));
  };

  // Build categorized subnets from destinations API
  const categorizedSubnets = useMemo(() => {
    if (!destinations) return null;
    const groups: Record<string, { subnet: string; label: string }[]> = {};
    for (const s of destinations.subnets) {
      const key = s.source;
      if (!groups[key]) groups[key] = [];
      groups[key].push({
        subnet: s.subnet,
        label: `${s.subnet} — ${s.peerName || s.sourceName || s.source}`,
      });
    }
    return groups;
  }, [destinations]);

  const actionLabel = (actionType: string) => {
    switch (actionType) {
      case 'allow': return 'Allow';
      case 'drop': return 'Drop';
      case 'rate_limit': return 'Rate Limit';
      default: return actionType;
    }
  };

  const footer = step === 2 ? (
    <div className="flex items-center justify-between w-full">
      <button type="button" onClick={() => setStep(1)} className="btn-secondary">
        Back
      </button>
      <div className="flex items-center space-x-3">
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        <button
          onClick={handleApply}
          disabled={applying}
          className="btn-primary inline-flex items-center space-x-2"
        >
          {applying ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
          <span>{applying ? 'Applying...' : 'Apply Template'}</span>
        </button>
      </div>
    </div>
  ) : undefined;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={step === 1 ? 'Apply Access Policy Template' : `Configure: ${selectedTemplate?.name}`}
      size="lg"
      footer={footer}
    >
      {step === 1 ? (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Select a DG Access Template to apply to <strong>{groupName}</strong>. The template will be cloned with edge-specific subnet values.
          </p>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : templates.length === 0 ? (
            <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-6 text-center">
              <p>No DG Access Templates available.</p>
              <p className="mt-1 text-xs">Create templates with scope "Device Group Access Template" in Routing Policies first.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleSelectTemplate(template)}
                  className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-primary-300 transition-colors text-left"
                >
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{template.name}</div>
                    {template.description && (
                      <div className="text-xs text-gray-500 mt-0.5">{template.description}</div>
                    )}
                    <div className="flex items-center space-x-3 mt-1">
                      <span className="text-xs text-gray-500">
                        Priority: {template.priority}
                      </span>
                      <span className="text-xs text-gray-500">
                        {template.rules?.length || 0} rule{(template.rules?.length || 0) !== 1 ? 's' : ''}
                      </span>
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        template.default_action === 'deny' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                      }`}>
                        Default: {template.default_action === 'deny' ? 'Deny (Zero Trust)' : 'Allow'}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 ml-4 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-blue-50 border-l-4 border-blue-400 p-3">
            <p className="text-sm text-blue-700">
              Map each rule's destination to a real subnet from the edge's network. The applied policy will start <strong>disabled</strong> — enable it when ready.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {selectedTemplate?.rules && selectedTemplate.rules.length > 0 ? (
                selectedTemplate.rules.map((rule) => (
                  <div key={rule.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-mono text-gray-500">#{rule.rule_order}</span>
                        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                          rule.action_type === 'allow' ? 'bg-green-100 text-green-700' :
                          rule.action_type === 'drop' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {actionLabel(rule.action_type)}
                        </span>
                        {rule.bandwidth_limit && (
                          <span className="text-xs text-gray-500">{rule.bandwidth_limit} Mbps</span>
                        )}
                      </div>
                      {rule.destination_subnet && (
                        <span className="text-xs text-gray-400 font-mono">
                          Template: {rule.destination_subnet}
                        </span>
                      )}
                    </div>
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-600">Destination Subnet</label>
                      <select
                        value={ruleOverrides[rule.rule_order]?.destination_subnet || ''}
                        onChange={(e) => handleOverrideChange(rule.rule_order, e.target.value)}
                        className="input-base w-full text-sm"
                      >
                        <option value="">Select a subnet...</option>
                        {categorizedSubnets ? (
                          <>
                            {categorizedSubnets['WAN Interface'] && (
                              <optgroup label="WAN Interfaces">
                                {categorizedSubnets['WAN Interface'].map((s, i) => (
                                  <option key={`wan-${i}`} value={s.subnet}>{s.label}</option>
                                ))}
                              </optgroup>
                            )}
                            {categorizedSubnets['LAN Interface'] && (
                              <optgroup label="LAN Interfaces">
                                {categorizedSubnets['LAN Interface'].map((s, i) => (
                                  <option key={`lan-${i}`} value={s.subnet}>{s.label}</option>
                                ))}
                              </optgroup>
                            )}
                            {categorizedSubnets['E2E Peering'] && (
                              <optgroup label="E2E Peering">
                                {destinations?.meta.hasPeering && (
                                  <option value="peer:*">Any Peer — all learned routes</option>
                                )}
                                {categorizedSubnets['E2E Peering'].map((s, i) => (
                                  <option key={`peer-${i}`} value={s.subnet}>{s.label}</option>
                                ))}
                              </optgroup>
                            )}
                            {categorizedSubnets['Static Route'] && (
                              <optgroup label="Static Routes">
                                {categorizedSubnets['Static Route'].map((s, i) => (
                                  <option key={`sr-${i}`} value={s.subnet}>{s.label}</option>
                                ))}
                              </optgroup>
                            )}
                            {categorizedSubnets['Connector'] && (
                              <optgroup label="Connectors">
                                {categorizedSubnets['Connector'].map((s, i) => (
                                  <option key={`conn-${i}`} value={s.subnet}>{s.label}</option>
                                ))}
                              </optgroup>
                            )}
                            {categorizedSubnets['Internet / Default Route'] && (
                              <optgroup label="Special">
                                {categorizedSubnets['Internet / Default Route'].map((s, i) => (
                                  <option key={`special-${i}`} value={s.subnet}>{s.label}</option>
                                ))}
                              </optgroup>
                            )}
                          </>
                        ) : (
                          <>
                            {routableSubnets.map((s) => (
                              <option key={s.subnet} value={s.subnet}>
                                {s.subnet} — {s.source}{s.edgeNames.length > 0 ? ` (${s.edgeNames.join(', ')})` : ''}
                              </option>
                            ))}
                            {rule.destination_subnet === '0.0.0.0/0' && !routableSubnets.some(s => s.subnet === '0.0.0.0/0') && (
                              <option value="0.0.0.0/0">0.0.0.0/0 — All traffic</option>
                            )}
                          </>
                        )}
                      </select>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4 text-center">
                  This template has no rules. The applied policy will only have the default action ({selectedTemplate?.default_action}).
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default ApplyDgapTemplateModal;
